// Changed-since-proposed detection (the "field staleness" phase of the drift
// strategy): a proposal's fhirpath-patch entry pins the exact base version it
// was drafted against (the `__seenBaseRef` sidecar). If any path the patch
// touches holds a different value at that base version than it does now, the
// world moved under the proposal in a way that matters — the reviewer is
// deciding against a diff the proposer never saw — so the entry is flagged
// for closer review instead of silently applying. Bookkeeping paths (meta
// tags, Task.lastModified) are exempt: they move on every touch and carry no
// clinical meaning, so treating them as drift would flag every proposal whose
// target got breathed on.

import { evalFhirPathTyped, toTypedValue } from '@medplum/core'
import type { BundleEntry, Resource } from '@medplum/fhirtypes'
import { isEqual } from 'lodash-es'
import {
  getFhirPathPatchSidecars,
  isFhirPathPatchParameters,
  parametersToFhirPathPatchOperations,
} from '#src/fhirpath-patch/index.ts'
import { isProposalBookkeepingPath } from '#src/queued-edits/bookkeeping.ts'

// The non-bookkeeping FHIRPaths a patch entry touches. An `add` op's target
// element is `path.name`; every other op targets `path` itself.
function touchedDomainPaths(entry: BundleEntry): string[] {
  if (!isFhirPathPatchParameters(entry.resource)) {
    return []
  }
  const paths = parametersToFhirPathPatchOperations(entry.resource).map((op) =>
    op.type === 'add' && op.name ? `${op.path}.${op.name}` : op.path
  )
  return [...new Set(paths.filter((path) => !isProposalBookkeepingPath(path)))]
}

// Whether a PATCH entry's touched domain paths changed between the base
// version the proposal was drafted against and the current version.
//
//   true      — a touched path's value moved since draft time (real drift)
//   false     — no touched path moved (any interim writes were unrelated)
//   undefined — not evaluable: Binary json-patch entry (no base pin), the
//               base version was not supplied, no current state, or a path
//               failed to evaluate
//
// `bases` is keyed by the entry's `__seenBaseRef` history reference string —
// callers load the pinned versions (immutable, cache forever) and pass them
// in; this stays transport-free.
export function entryChangedSinceProposed(
  entry: BundleEntry,
  current: Resource | undefined,
  bases: Record<string, Resource>
): boolean | undefined {
  if (!current || !isFhirPathPatchParameters(entry.resource)) {
    return undefined
  }
  const { baseRef } = getFhirPathPatchSidecars(entry.resource)
  const base = baseRef ? bases[baseRef] : undefined
  if (!base) {
    return undefined
  }
  if (base.meta?.versionId === current.meta?.versionId) {
    return false
  }
  const typedBase = [toTypedValue(base)]
  const typedCurrent = [toTypedValue(current)]
  for (const path of touchedDomainPaths(entry)) {
    try {
      const baseValue = evalFhirPathTyped(path, typedBase).map((v) => v.value)
      const currentValue = evalFhirPathTyped(path, typedCurrent).map(
        (v) => v.value
      )
      if (!isEqual(baseValue, currentValue)) {
        return true
      }
    } catch {
      return undefined
    }
  }
  return false
}

// The `__seenBaseRef` history reference strings pinned by a bundle's
// fhirpath-patch entries — the versions a caller must load (immutable; cache
// forever) to run `entryChangedSinceProposed`.
export function bundleBaseRefs(entries: BundleEntry[]): string[] {
  const refs = new Set<string>()
  for (const entry of entries) {
    if (!isFhirPathPatchParameters(entry.resource)) {
      continue
    }
    const { baseRef } = getFhirPathPatchSidecars(entry.resource)
    if (baseRef) {
      refs.add(baseRef)
    }
  }
  return [...refs]
}
